import { memo, useMemo, Fragment, useCallback, useContext } from 'react';
import styled from '@emotion/styled';
import { type Options as MarkdownOptions } from 'react-markdown';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { ChatQueryStatus, ChatResponseType } from '@trinity/chat';
import { ReportFormat } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import { ScrollableTable } from '@trinity/chat-components/components/Markdown/ScrollableTable';
import { ActiveChatSessionContext } from './context';
import { useDownloadQueryTable } from './hooks/useDownloadQueryTable';
import { BaseDownloadButton } from './Message.Download';
import { ChartQuantAnalysesResponse } from './Message.Response.QuantAnalysis';
import { FindQuantAnalysesResponse } from './Message.Response.FindQuantAnalysis';
import { FindQuotesResponse } from './Message.Response.Quotes';
import { RoutingResult } from './Message.Response.RoutingResult';
import type { ResponseItem } from './Message.Response.utils';
import { parseResponse } from './Message.Response.utils';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { ChartResponse } from './Message.Response.Chart';

type MessageResponse<T extends ChatResponseType = ChatResponseType> = {
  queryIdentifier: string;
  item: Chat.QueryResponse<T> | null;
  state: Chat.QueryState.Value<T>;
  status: ChatQueryStatus;
};

export const MessageResponse = ({ item, state, status, queryIdentifier }: MessageResponse) => {

  if (!item) {
    return null;
  }

  switch (item.type) {
    case ChatResponseType.Text:
      return <MessageResponseRawText value={item.value} />;
    case ChatResponseType.Markdown:
      return (
        <Markdown
          queryIdentifier={queryIdentifier}
          value={item.value}
          allowHtml={false}
          showDownloadButton={status !== ChatQueryStatus.Streaming}
        />
      );
    case ChatResponseType.MarkdownHtml:
      return (
        <Markdown
          queryIdentifier={queryIdentifier}
          value={item.value}
          allowHtml={true}
          trustedDomains={['*']}
          showDownloadButton={status !== ChatQueryStatus.Streaming}
        />
      );

    case ChatResponseType.LocationSelection:
      return null;
    case ChatResponseType.Chart:
      return <ChartResponse />;
    case ChatResponseType.BIChartQuantAnalyses:
      return <ChartQuantAnalysesResponse />;
    case ChatResponseType.LocationRouting:
      return <RoutingResult item={item} state={state as Chat.QueryState.Value<ChatResponseType.LocationRouting>} queryIdentifier={queryIdentifier} />;

    case ChatResponseType.BIFindQuotes:
      return <FindQuotesResponse data={item.data} />;

    case ChatResponseType.BIFindQuantAnalyses:
      return <FindQuantAnalysesResponse data={item.data} />;

    case ChatResponseType.HcpAvailability:
    case ChatResponseType.HcpSummary:
    case ChatResponseType.HcpSelection:
    case ChatResponseType.SchedulingAssistant:
    case ChatResponseType.SmartRouting:
    case ChatResponseType.FormWorkflow:
    case ChatResponseType.SegmentationTagsNeeded:
    case ChatResponseType.SegmentationQuestionsNeeded:
    case ChatResponseType.SegmentationComplete:
    case ChatResponseType.SegmentationCancelled:
      return null; //this is implemented at top level

    default:
      throw new UnreachableCaseError(item);
  }
};

type MarkdownProps = {
  queryIdentifier: string;
  value: string;
  allowHtml?: boolean;
  trustedDomains?: string[];
  showDownloadButton?: boolean;
};

const Markdown = memo(({ queryIdentifier, value, allowHtml, trustedDomains, showDownloadButton = true }: MarkdownProps) => {

  const session = useContext(ActiveChatSessionContext);
  const features = useContext(GroupFeaturesContext);

  const { download } = useDownloadQueryTable({
    chatInstanceId: session.id,
    queryIdentifier,
  });

  const handleDownload = useCallback(() => {
    download({
      title: `Generating Export`,
      name: `Table Data`,
      extension: ReportFormat.Excel,
    });
  }, [download]);

  const customMarkdownComponents = useMemo((): MarkdownOptions['components'] => ({
    table: props => {
      return (
        <>
          <ScrollableTable>
            {props.children}
          </ScrollableTable>
          {features.exportChatData && showDownloadButton && (
            <DownloadButtonWrapper>
              <BaseDownloadButton
                label="Download List"
                onClick={handleDownload} />
            </DownloadButtonWrapper>
          )}
        </>
      );
    },
  }), [features.exportChatData, handleDownload, showDownloadButton]);

  return (
    // Markdown with iFrame
    <ChatMarkdown
      components={customMarkdownComponents}
      value={value}
      allowHtml={allowHtml}
      trustedDomains={trustedDomains}
    />
  );
});

type MessageResponseRawTextProps = {
  value: string;
};

export const MessageResponseRawText = memo(({ value }: MessageResponseRawTextProps) => {

  const items = useMemo(() => {
    return parseResponse(value);
  }, [value]);

  const renderItem = useCallback((item: ResponseItem, i: number) => {
    switch (item.type) {
      case 'bullets': {
        return (
          <StyledUl key={i}>
            {item.value.map((value, j) => (
              <StyledLi key={j}>{value}</StyledLi>
            ))}
          </StyledUl>
        );
      }
      case 'numbered': {
        return (
          <StyledOl key={i}>
            {item.value.map((value, j) => (
              <StyledLi key={j}>{value}</StyledLi>
            ))}
          </StyledOl>
        );
      }
      case 'text': {
        if (item.value === '') return <br key={i} />;
        return (
          <div key={i}>{item.value}</div>
        );
      }
    }
  }, []);

  return (
    <Fragment>
      {items.map(renderItem)}
    </Fragment>
  );
});

const StyledLi = styled('li')({
  marginBottom: 4,

  '&:last-child': {
    marginBottom: 0,
  },
});

const StyledOl = styled('ol')({
  paddingLeft: 20,

  [`& > li`]: {
    paddingLeft: 5,
    marginBottom: 8,
    '&::marker': {
      fontFamily: 'var(--font-semibold)',
    },
  },
});

const StyledUl = styled('ul')({
  paddingInlineStart: 20,
});

const DownloadButtonWrapper = styled.div({
  marginTop: 8,
  marginBottom: 16,
});
